import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';

import { hasRoles, DOWNLOAD_REPORTS_ROLES } from '../../../util/RoleUtil';

import { retrieveQualityControlInputs } from '../../../services/ReportService';

import { SaveButton } from '../../common/button/save-button/SaveButton';
import { SiteQueryStatus } from './site-query-status/SiteQueryStatus';
import { SiteQueryStatusPerMonth } from './site-query-status-per-month/SiteQueryStatusPerMonth';
import { QueriesPerStaff } from './queries-per-staff/QueriesPerStaff';
import { QueryAge } from './query-age/QueryAge';
import { QueriesPerType } from './queries-per-type/QueriesPerType';
import { QueryResolutionTAT } from './query-resolution-tat/QueryResolutionTAT';
import { ParticipantReviews } from './participant-reviews/ParticipantReviews';

import './QualityControl.css';

const PARTICIPANT_REVIEWS_TAB = 'participant-reviews';

export const QualityControl = ({ context, setSpinnerText, showToaster }) => {
  const [showFilters, setShowFilters] = useState(true);
  const [provinces, setProvinces] = useState([]);
  const [province, setProvince] = useState(undefined);
  const [district, setDistrict] = useState(undefined);
  const [subDistrict, setSubDistrict] = useState(undefined);
  const [facility, setFacility] = useState(undefined);
  const [years, setYears] = useState([]);
  const [activeTab, setActiveTab] = useState('site-query-status');

  const tabs = useRef(new Map()).current;

  const reversedYears = useMemo(() => {
    if (!facility || !facility.years) return [];

    return [...facility.years].reverse();
  }, [facility]);

  useEffect(() => {
    const canDownloadReports = hasRoles(context, DOWNLOAD_REPORTS_ROLES);

    tabs.set(
      'site-query-status',
      <SiteQueryStatus
        facility={facility}
        dates={years}
        resetFilters={resetFilters}
        context={context}
        setSpinnerText={setSpinnerText}
        showToaster={showToaster}
        downloadEnabled={canDownloadReports}
      />
    );
    tabs.set(
      'site-query-status-per-month',
      <SiteQueryStatusPerMonth
        facility={facility}
        dates={years}
        resetFilters={resetFilters}
        context={context}
        setSpinnerText={setSpinnerText}
        showToaster={showToaster}
        downloadEnabled={canDownloadReports}
      />
    );
    tabs.set(
      'queries-per-staff',
      <QueriesPerStaff
        facility={facility}
        dates={years}
        resetFilters={resetFilters}
        context={context}
        setSpinnerText={setSpinnerText}
        showToaster={showToaster}
        downloadEnabled={canDownloadReports}
      />
    );
    tabs.set(
      'query-age',
      <QueryAge
        facility={facility}
        dates={years}
        resetFilters={resetFilters}
        context={context}
        setSpinnerText={setSpinnerText}
        showToaster={showToaster}
        downloadEnabled={canDownloadReports}
      />
    );
    tabs.set(
      'queries-per-type',
      <QueriesPerType
        facility={facility}
        dates={years}
        resetFilters={resetFilters}
        context={context}
        setSpinnerText={setSpinnerText}
        showToaster={showToaster}
        downloadEnabled={canDownloadReports}
      />
    );
    tabs.set(
      'query-resolution-tat',
      <QueryResolutionTAT
        facility={facility}
        dates={years}
        resetFilters={resetFilters}
        context={context}
        setSpinnerText={setSpinnerText}
        showToaster={showToaster}
        downloadEnabled={canDownloadReports}
      />
    );
    tabs.set(
      PARTICIPANT_REVIEWS_TAB,
      <ParticipantReviews
        facility={facility}
        dates={years}
        resetFilters={resetFilters}
        context={context}
        setSpinnerText={setSpinnerText}
        showToaster={showToaster}
        downloadEnabled={canDownloadReports}
      />
    );
  }, [context, facility, setSpinnerText, showToaster, tabs, years]);

  const retrieveLocations = useCallback(async () => {
    setSpinnerText('Retrieving Locations');
    const [data, error] = await retrieveQualityControlInputs(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        setProvinces(data.qualityControlProvinceDropdownVOS);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  }, [context, setSpinnerText, showToaster]);

  useEffect(() => {
    retrieveLocations();
  }, [retrieveLocations]);

  const handleProvinceChange = (province) => {
    setProvince(province);
    setDistrict(null);
    setSubDistrict(null);
    setFacility(null);
    setYears([]);
  };

  const handleDistrictChange = (district) => {
    setDistrict(district);
    setSubDistrict(null);
    setFacility(null);
    setYears([]);
  };

  const handleSubDistrictChange = (subDistrict) => {
    setSubDistrict(subDistrict);
    setFacility(null);
    setYears([]);
  };

  const handleFacilityChange = (facility) => {
    setFacility(facility);
    setYears([]);
  };

  const handleGenerateClick = () => {
    setShowFilters(false);
    setActiveTab('site-query-status');
  };

  const resetFilters = () => {
    setProvince(null);
    setDistrict(null);
    setSubDistrict(null);
    setFacility(null);
    setYears([]);
    setShowFilters(true);
  };

  return (
    <section className="quality-control">
      {showFilters ? (
        <section className="quality-control-filters">
          <section className="quality-control-inputs">
            <p>Generate Report</p>
            <div className="input-container">
              <label className="input-label">Province:</label>
              <Select
                options={provinces}
                value={province}
                onChange={handleProvinceChange}
                className="input-select"
                classNamePrefix="input-select"
                getOptionLabel={(option) => option.province}
                getOptionValue={(option) => option.province}
                closeMenuOnSelect
              />
            </div>
            <div className="input-container">
              <label className="input-label">District:</label>
              <Select
                options={province?.districts || []}
                value={district}
                onChange={handleDistrictChange}
                className="input-select"
                classNamePrefix="input-select"
                getOptionLabel={(option) => option.district}
                getOptionValue={(option) => option.district}
                closeMenuOnSelect
              />
            </div>
            <div className="input-container">
              <label className="input-label">Sub-District:</label>
              <Select
                options={district?.subDistricts || []}
                value={subDistrict}
                onChange={handleSubDistrictChange}
                className="input-select"
                classNamePrefix="input-select"
                getOptionLabel={(option) => option.subDistrict}
                getOptionValue={(option) => option.subDistrict}
                closeMenuOnSelect
              />
            </div>
            <div className="input-container">
              <label className="input-label">Facility:</label>
              <Select
                options={subDistrict?.facilities || []}
                value={facility}
                onChange={handleFacilityChange}
                className="input-select"
                classNamePrefix="input-select"
                getOptionLabel={(option) => option.facility}
                getOptionValue={(option) => option.externalReference}
                closeMenuOnSelect
              />
            </div>
            <div className="input-container quality-control-year-dropdown">
              <label className="input-label">Years:</label>
              <Select
                options={reversedYears}
                value={years}
                onChange={setYears}
                className="input-select"
                classNamePrefix="input-select"
                getOptionLabel={(option) => option}
                getOptionValue={(option) => option}
                closeMenuOnSelect={false}
                isMulti
              />
            </div>
          </section>
          <SaveButton
            title="Generate"
            color="#077187"
            onClick={handleGenerateClick}
            disabled={!facility || !years || years.length === 0}
          />
        </section>
      ) : (
        <>
          <section className="quality-control-tabs">
            <button
              className={
                'quality-control-tab site-query-status ' + (activeTab === 'site-query-status' ? 'active-tab' : null)
              }
              onClick={() => setActiveTab('site-query-status')}
            >
              Site Query Status
            </button>
            <button
              className={
                'quality-control-tab site-query-status-per-month ' +
                (activeTab === 'site-query-status-per-month' ? 'active-tab' : null)
              }
              onClick={() => setActiveTab('site-query-status-per-month')}
            >
              Site Query Status per Month
            </button>
            <button
              className={
                'quality-control-tab queries-per-staff ' + (activeTab === 'queries-per-staff' ? 'active-tab' : null)
              }
              onClick={() => setActiveTab('queries-per-staff')}
            >
              Queries per Staff
            </button>
            <button
              className={'quality-control-tab query-age ' + (activeTab === 'query-age' ? 'active-tab' : null)}
              onClick={() => setActiveTab('query-age')}
            >
              Query Age
            </button>
            <button
              className={
                'quality-control-tab queries-per-type ' + (activeTab === 'queries-per-type' ? 'active-tab' : null)
              }
              onClick={() => setActiveTab('queries-per-type')}
            >
              Queries per Type
            </button>
            <button
              className={
                'quality-control-tab query-resolution-tat ' +
                (activeTab === 'query-resolution-tat' ? 'active-tab' : null)
              }
              onClick={() => setActiveTab('query-resolution-tat')}
            >
              Query Resolution TaT
            </button>
            <button
              className={
                'quality-control-tab participant-reviews ' +
                (activeTab === PARTICIPANT_REVIEWS_TAB ? 'active-tab' : null)
              }
              onClick={() => setActiveTab(PARTICIPANT_REVIEWS_TAB)}
            >
              Participant Reviews
            </button>
          </section>
          <section className={'quality-control-content ' + activeTab}>{tabs.get(activeTab)}</section>
        </>
      )}
    </section>
  );
};
